import { Grid, GridColumn, GridRow } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpCard } from 'components/BpCard/BpCard';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { BpText } from 'components/BpText/BpText';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../utils/dateCalculations';
import { BpTipTapText } from '../BpTipTapText/BpTipTapText';
import { FileTable, FileTableFieldsDocument } from '../FileTable/FileTable';

const WorkMaterialDetailFieldsDocument = graphql(`
  fragment WorkMaterialDetailFields on WorkMaterial {
    uuid
    __typename
    title
    visibleFrom {
      uuid
      __typename
      date
    }
    visibleTo {
      uuid
      __typename
      date
    }
    material {
      uuid
      __typename
      text
      fileEntries {
        uuid
        __typename
        ...FileTableFields
      }
    }
  }
`);

export type MaterialDetailProps = {
  onClose: () => void;
  isGroupEditor: boolean;
  data: FragmentType<typeof WorkMaterialDetailFieldsDocument>;
};

export const WorkMaterialDetail: FC<MaterialDetailProps> = ({ onClose, isGroupEditor, data }) => {
  const { t } = useTranslation();
  const material = getFragmentData(WorkMaterialDetailFieldsDocument, data);
  const files = getFragmentData(FileTableFieldsDocument, material?.material.fileEntries);

  return (
    <>
      <BpHeader
        headline={material?.title ?? ''}
        actions={[
          {
            hierarchy: 'primary',
            callback: () => onClose(),
            text: t('common.back'),
          },
        ]}
      />
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={8}>
            <BpCard noPadding header={{ headline: t('common.content') }}>
              {material?.material.text && (
                <BpTipTapText className='mb-4' customPadding='0 var(--spacing-6)' content={material?.material.text} />
              )}
              <FileTable files={files} mode={'show'} isGroupEditor={isGroupEditor} fullHeight />
            </BpCard>
          </GridColumn>
          <GridColumn width={4}>
            <BpCard header={{ headline: t('workmaterials.relevantTimeFrame') }}>
              <BpText embedded>{`${niceDate(material?.visibleFrom?.date, 'short')} - ${niceDate(
                material?.visibleTo?.date,
                'short',
              )}`}</BpText>
            </BpCard>
          </GridColumn>
        </GridRow>
      </Grid>
    </>
  );
};
